import type { RequestContext } from "../../../application/context/request-context.js";
import { getActiveClientId } from "../../../repositories/tenant-guard.js";
import { AppError, ErrorCode } from "../../../shared/errors/app-error.js";
import type { InspectionBookRepository } from "../../../repositories/operational-repositories.js";
import type { InspectionBook } from "../../../domain/inspection-book/inspection-book.js";
import type { ValidationIssue } from "../../../domain/inspection-book/validation-issue.js";
import type { EngineerDecision, ApprovedEngineeringPattern } from "../../../domain/inspection-book/engineer-decision.js";
import type { InspectionBookId, InspectionId } from "../../../shared/kernel/identifiers.js";

export class InMemoryInspectionBookRepository implements InspectionBookRepository {
  private books: Map<string, InspectionBook> = new Map();
  private issues: Map<string, ValidationIssue[]> = new Map();
  private decisions: Map<string, EngineerDecision[]> = new Map();
  private patterns: Map<string, ApprovedEngineeringPattern[]> = new Map();

  async findById(context: RequestContext, id: InspectionBookId): Promise<InspectionBook | null> {
    const clientId = getActiveClientId(context);
    const book = this.books.get(`${clientId}_${id}`);
    return book ? JSON.parse(JSON.stringify(book)) : null;
  }

  async findByInspectionId(context: RequestContext, inspectionId: InspectionId): Promise<InspectionBook | null> {
    const clientId = getActiveClientId(context);
    for (const b of this.books.values()) {
      if (b.clientId === clientId && b.inspectionId === inspectionId) {
        return JSON.parse(JSON.stringify(b));
      }
    }
    return null;
  }

  async listAll(context: RequestContext): Promise<readonly InspectionBook[]> {
    const clientId = getActiveClientId(context);
    const results: InspectionBook[] = [];
    for (const b of this.books.values()) {
      if (b.clientId === clientId) results.push(JSON.parse(JSON.stringify(b)));
    }
    return results;
  }

  async save(context: RequestContext, book: InspectionBook): Promise<void> {
    const clientId = getActiveClientId(context);
    if (book.clientId !== clientId) throw new AppError(ErrorCode.Authorization, 'Client mismatch');
    this.books.set(`${clientId}_${book.id}`, JSON.parse(JSON.stringify(book)));
  }

  async delete(context: RequestContext, id: InspectionBookId): Promise<void> {
    const clientId = getActiveClientId(context);
    this.books.delete(`${clientId}_${id}`);
  }

  async saveValidationIssue(context: RequestContext, issue: ValidationIssue): Promise<void> {
    const clientId = getActiveClientId(context);
    if (issue.clientId !== clientId) throw new AppError(ErrorCode.Authorization, 'Client mismatch');

    const list = this.issues.get(issue.inspectionId) || [];
    const idx = list.findIndex(i => i.id === issue.id);
    if (idx >= 0) {
      list[idx] = JSON.parse(JSON.stringify(issue));
    } else {
      list.push(JSON.parse(JSON.stringify(issue)));
    }
    this.issues.set(issue.inspectionId, list);
  }

  async listValidationIssues(context: RequestContext, inspectionId: InspectionId): Promise<readonly ValidationIssue[]> {
    return (this.issues.get(inspectionId) || []).map(i => JSON.parse(JSON.stringify(i)));
  }

  async saveEngineerDecision(context: RequestContext, decision: EngineerDecision): Promise<void> {
    const clientId = getActiveClientId(context);
    if (decision.clientId !== clientId) throw new AppError(ErrorCode.Authorization, 'Client mismatch');

    const list = this.decisions.get(clientId) || [];
    list.unshift(JSON.parse(JSON.stringify(decision)));
    this.decisions.set(clientId, list);
  }

  async listEngineerDecisions(context: RequestContext, inspectionId?: InspectionId): Promise<readonly EngineerDecision[]> {
    const clientId = getActiveClientId(context);
    const list = this.decisions.get(clientId) || [];
    if (inspectionId) {
      return list.filter(d => d.inspectionId === inspectionId).map(d => JSON.parse(JSON.stringify(d)));
    }
    return list.map(d => JSON.parse(JSON.stringify(d)));
  }

  async saveApprovedPattern(context: RequestContext, pattern: ApprovedEngineeringPattern): Promise<void> {
    const clientId = getActiveClientId(context);
    if (pattern.clientId !== clientId) throw new AppError(ErrorCode.Authorization, 'Client mismatch');

    const list = this.patterns.get(clientId) || [];
    const idx = list.findIndex(p => p.id === pattern.id);
    if (idx >= 0) {
      list[idx] = JSON.parse(JSON.stringify(pattern));
    } else {
      list.push(JSON.parse(JSON.stringify(pattern)));
    }
    this.patterns.set(clientId, list);
  }

  async listApprovedPatterns(context: RequestContext): Promise<readonly ApprovedEngineeringPattern[]> {
    const clientId = getActiveClientId(context);
    return (this.patterns.get(clientId) || []).map(p => JSON.parse(JSON.stringify(p)));
  }
}
